import { useMemo, useState } from 'react';

import { useVendors, useItems, useItemVariantAxesFor, type Item } from '@/api/masters';
import type { ItemVariantAxisOptions } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { apiFetch } from '@/api/client';
import { uploadItemMedia, uploadUnitMedia } from '@/api/upload';
import { DatePicker } from '@/components/DatePicker';
import { DesignPhoto } from '@/components/DesignPhoto';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass } from '@/components/Field';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import type { AddItemUnitBatchResult } from '@core/api/masters';
import { typedNumber } from '@core/catalogue/forms';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import type { UnitCondition } from '@core/generated/enums';
import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { toBatchRequest } from '@core/units/intake';
import {
  addReceiveLine,
  addSizes,
  chooseDesign,
  countedLooks,
  countOf,
  emptyReceive,
  lineCount,
  lookColour,
  lookKey,
  lookName,
  maxPerSize,
  receiveCount,
  receiveLabel,
  receiveGarments,
  receiveProblems,
  receiveSummary,
  removeReceiveLine,
  setAddedCount,
  sizeChoices,
  splitAxes,
  toIntakeDraft,
  updateReceiveLine,
  type ReceiveDraft,
  type ReceiveLine,
} from '@core/units/receive';

import { ReceiveGarments } from './ReceiveGarments';
import { ReceivedStock } from './ReceivedStock';

/** The conditions a delivery arrives in, best first — a new delivery is almost always New. */
const conditions: readonly UnitCondition[] = ['New', 'Excellent', 'Good', 'Fair', 'Poor'];

/**
 * New stock, **the phone's flow on the website** (owner, 2026-10-23: "like mobile flow in web"): a
 * delivery said the way a storeman counts it off the boxes — the design, its colour, and how many of
 * each size — then the codes the server issues, a photograph of each, and the tags.
 *
 * <p>
 * <strong>One set of rules with the phone.</strong> The draft is the core's `units/receive.ts`, which
 * turns into the intake draft and its one batch request (`toIntakeDraft` → `toBatchRequest`), so the
 * two clients cannot book stock in differently. Prices are the phone's too: rent for the event and
 * an optional extra day (`SetEventRateAsync`), never a day rate.
 * </p>
 *
 * <p>
 * The per-line grid (`StockIntakePage`) stays for what this flow does not say: garments that
 * already carry tags, and receiving against a purchase order, whose lines are fixed by the order.
 * </p>
 */
export function ReceiveStockPage({
  session,
  today,
  currency = '₹',
  invalidates,
  onClose,
  onDetailed,
}: {
  session: Session;
  today: string;
  currency?: string;
  /** What booking in refreshes — the units list, its variants, the goods receipts. */
  invalidates: readonly (readonly unknown[])[];
  onClose: () => void;
  /** Opens the per-line form, for garments that already carry tags. */
  onDetailed: () => void;
}) {
  const branchId = session.branch?.id;
  const [draft, setDraft] = useState<ReceiveDraft>(() => emptyReceive(today));
  // Each chosen design, kept as picked: a line's search moving on must not blank its name or photo.
  const [designs, setDesigns] = useState<Record<number, Item>>({});
  // Photographs of each line's design, sent to its gallery once the stock exists.
  const [photos, setPhotos] = useState<Record<string, File[]>>({});
  const [photoFailures, setPhotoFailures] = useState(0);
  const [sendingPhotos, setSendingPhotos] = useState(false);
  const [received, setReceived] = useState<AddItemUnitBatchResult | null>(null);
  // Step 2 (canvas "Garment numbers flow"): each garment's own photo and the shop's own number.
  const [step, setStep] = useState<'what' | 'garments'>('what');
  const [references, setReferences] = useState<Record<string, string>>({});
  const [unitPhotos, setUnitPhotos] = useState<Record<string, File>>({});
  const [photographed, setPhotographed] = useState<ReadonlySet<number>>(new Set());

  const mayRate = canAct(session, 'RateCard:Create');
  const mayPhotograph = canAct(session, 'Item:Edit');

  const itemIds = useMemo(
    () => [
      ...new Set(draft.lines.map((line) => line.itemId).filter((id): id is number => id !== null)),
    ],
    [draft.lines],
  );
  const { axes } = useItemVariantAxesFor(itemIds);

  const firstField = useAutoFocusFirstField<HTMLDivElement>();

  const bookIn = useApiMutation<AddItemUnitBatchResult, ReceiveDraft>({
    mutationFn: (current) =>
      apiFetch<AddItemUnitBatchResult>('/api/item-unit/batch', {
        method: 'POST',
        body: toBatchRequest(toIntakeDraft(current, axes, references)),
      }),
    invalidates,
    onSuccess: (result) => void afterBookingIn(result),
  });

  /**
   * The stock is booked in first; then each line's photographs go to its design's gallery — the
   * garments borrow the design's picture until they have their own. A photograph that fails is
   * counted and said on the next screen; the stock is in either way.
   */
  const afterBookingIn = async (result: AddItemUnitBatchResult) => {
    setSendingPhotos(true);
    let failed = 0;
    const sent = new Set<number>();

    // Each garment's own photo to that garment: the result lists them in the order step 2 did.
    for (const garment of receiveGarments(draft, axes)) {
      const file = unitPhotos[garment.key];
      const unit = result.lines[garment.lineIndex]?.units[garment.position];

      if (file && unit) {
        try {
          await uploadUnitMedia(unit.id, file);
          sent.add(unit.id);
        } catch {
          failed += 1;
        }
      }
    }

    for (const line of draft.lines) {
      for (const file of line.itemId === null ? [] : (photos[line.key] ?? [])) {
        try {
          await uploadItemMedia(line.itemId!, file);
        } catch {
          failed += 1;
        }
      }
    }

    setPhotoFailures(failed);
    setPhotographed(sent);
    setSendingPhotos(false);
    setReceived(result);
  };

  if (received) {
    return (
      <ReceivedStock
        session={session}
        result={received}
        photoFailures={photoFailures}
        photographed={photographed}
        onMore={() => {
          setReceived(null);
          setDraft(emptyReceive(today));
          setPhotos({});
          setPhotoFailures(0);
          setStep('what');
          setReferences({});
          setUnitPhotos({});
          setPhotographed(new Set());
        }}
        onDone={onClose}
      />
    );
  }

  const problems = receiveProblems(draft, axes, (id) => designs[id]?.name ?? 'this design');
  const count = receiveCount(draft);
  const summary = receiveSummary(draft);
  const busy = bookIn.isPending || sendingPhotos;
  const bookLabel = bookIn.isPending
    ? 'Booking in…'
    : sendingPhotos
      ? 'Sending the photos…'
      : receiveLabel(count);

  if (step === 'garments' && problems.length === 0) {
    return (
      <ReceiveGarments
        draft={draft}
        axes={axes}
        designs={designs}
        references={references}
        onReferences={setReferences}
        photos={unitPhotos}
        onPhotos={setUnitPhotos}
        mayPhotograph={canAct(session, 'ItemUnit:Edit')}
        busy={busy}
        bookLabel={bookLabel}
        error={bookIn.error}
        onBack={() => setStep('what')}
        onBookIn={() => bookIn.mutate(draft)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 lg:gap-4" {...enterKeyScope} ref={firstField}>
      <PageHeading
        title="New stock"
        subtitle={`Stock · ${session.branch?.name ?? 'this branch'} — a design, its colour, how many of each size`}
        actions={
          <>
            <Button onClick={onClose} disabled={busy} shortcut="back">
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              shortcut="save"
              data-enter-key-commit
              loading={busy}
              disabled={problems.length > 0 || busy}
              onClick={() => setStep('garments')}
            >
              Next: each garment
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-12 gap-3 lg:gap-4">
        <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-8">
          <Delivery draft={draft} branchId={branchId} busy={busy} onChange={setDraft} />

          {draft.lines.map((line) => (
            <LineCard
              key={line.key}
              line={line}
              branchId={branchId}
              design={line.itemId === null ? undefined : designs[line.itemId]}
              axes={line.itemId === null ? undefined : axes[line.itemId]}
              currency={currency}
              mayRate={mayRate}
              photos={mayPhotograph ? (photos[line.key] ?? []) : null}
              onPhotos={(files) => setPhotos((current) => ({ ...current, [line.key]: files }))}
              removable={draft.lines.length > 1 || line.itemId !== null}
              busy={busy}
              onPick={(design) => {
                setDesigns((current) => ({ ...current, [design.id]: design }));
                setDraft((current) => chooseDesign(current, line.key, design.id));
              }}
              onChange={(change) => setDraft((current) => change(current))}
            />
          ))}

          <button
            type="button"
            disabled={busy}
            onClick={() => setDraft((current) => addReceiveLine(current))}
            className="border-default hover:bg-light flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed bg-transparent text-[length:var(--scale-list-name)] font-semibold text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="icon icon-plus" />
            Another design in this delivery
          </button>
        </div>

        <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-4">
          <Card className="xl:sticky xl:top-24">
            <p className="text-sm font-semibold text-gray-900">This delivery</p>
            {count === 0 ? (
              <p className="text-default py-4 text-center text-[length:var(--scale-list-meta)]">
                Nothing counted yet — choose the design that came in, then how many of each size.
              </p>
            ) : (
              <>
                <p className="mt-2 mb-0 text-2xl font-bold text-gray-900">
                  {summary.garments} garment{summary.garments === 1 ? '' : 's'}
                </p>
                <p className="text-default text-[length:var(--scale-list-meta)]">
                  {[
                    `${summary.designs} design${summary.designs === 1 ? '' : 's'}`,
                    summary.cost === null ? null : `cost ${currency}${formatAmount(summary.cost)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <ul className="border-border-color mt-2 flex flex-col gap-1 border-t ps-0 pt-2">
                  {draft.lines.map((line) =>
                    line.itemId === null || lineCount(line) === 0 ? null : (
                      <li
                        key={line.key}
                        className="text-default flex list-none flex-col text-[length:var(--scale-list-meta)]"
                      >
                        <span className="flex justify-between gap-2">
                          <span className="truncate">{designs[line.itemId]?.name ?? 'Design'}</span>
                          <span className="shrink-0 text-gray-900">× {lineCount(line)}</span>
                        </span>
                        <span className="truncate text-[11px]">
                          {countedLooks(line)
                            .map((entry) =>
                              [
                                lookName(entry.look, axes[line.itemId!] ?? []),
                                countOf(entry.counts),
                              ]
                                .filter((part) => part !== '')
                                .join(' '),
                            )
                            .join(' · ')}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </>
            )}

            {problems[0] && count > 0 && (
              <p className="text-warning mt-2 mb-0 text-[length:var(--scale-list-meta)]">
                {problems[0]}
              </p>
            )}

            <div className="mt-3">
              <Button
                variant="primary"
                icon="icon icon-check"
                className="w-full justify-center"
                loading={busy}
                disabled={problems.length > 0 || busy}
                onClick={() => setStep('garments')}
              >
                Next: each garment
              </Button>
            </div>
            <p className="text-default mt-2 mb-0 text-[length:var(--scale-list-meta)]">
              Next, each garment gets its photo and your own number if it has one, then Book in.
            </p>
          </Card>

          <p className="text-default text-[length:var(--scale-list-meta)]">
            Garments that already carry their own tags?{' '}
            <button
              type="button"
              onClick={onDetailed}
              disabled={busy}
              className="text-primary cursor-pointer font-semibold hover:underline"
            >
              Book them in line by line
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The day it came in, who it was bought from, and their invoice — once for the delivery, not per
 * design. Only vendors marked as a supplier are offered; the server refuses the others.
 */
function Delivery({
  draft,
  branchId,
  busy,
  onChange,
}: {
  draft: ReceiveDraft;
  branchId: number | undefined;
  busy: boolean;
  onChange: (draft: ReceiveDraft) => void;
}) {
  const [supplier, setSupplier] = useState<PickerOption | null>(null);
  const [search, setSearch] = useState('');
  const suppliers = useVendors(branchId, { capability: 'Supplier', search, pageSize: 10 });
  const options: PickerOption[] = (suppliers.data?.items ?? []).map((vendor) => ({
    id: String(vendor.id),
    primary: vendor.name,
    secondary: vendor.mobile,
  }));

  return (
    <Card>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Came in on">
          <DatePicker
            className="w-full"
            value={toDayjs(draft.receivedOn) ?? null}
            disabled={busy}
            onChange={(value) => onChange({ ...draft, receivedOn: toIsoDate(value) ?? '' })}
          />
        </Field>
        <Field label="Supplier">
          <SearchPicker
            value={supplier}
            options={options}
            total={suppliers.data?.total ?? 0}
            loading={suppliers.isLoading}
            disabled={busy}
            placeholder="Search suppliers"
            onSearch={setSearch}
            onSelect={(option) => {
              setSupplier(option);
              onChange({ ...draft, supplierVendorId: option === null ? null : Number(option.id) });
            }}
          />
        </Field>
        <Field label="Invoice or note">
          <input
            value={draft.note}
            disabled={busy}
            onChange={(event) => onChange({ ...draft, note: event.target.value })}
            placeholder="Optional"
            className={controlClass}
          />
        </Field>
      </div>
    </Card>
  );
}

/** One design in the delivery: its colour, a count per size, and what it cost and rents for. */
function LineCard({
  line,
  branchId,
  design,
  axes,
  currency,
  mayRate,
  photos,
  onPhotos,
  removable,
  busy,
  onPick,
  onChange,
}: {
  line: ReceiveLine;
  branchId: number | undefined;
  design: Item | undefined;
  axes: ItemVariantAxisOptions[] | undefined;
  currency: string;
  mayRate: boolean;
  /** Photographs waiting to go to this design, or null for a role that may not add them. */
  photos: File[] | null;
  onPhotos: (files: File[]) => void;
  removable: boolean;
  busy: boolean;
  onPick: (design: Item) => void;
  onChange: (change: (draft: ReceiveDraft) => ReceiveDraft) => void;
}) {
  const [search, setSearch] = useState('');
  const [changing, setChanging] = useState(false);
  const matches = useItems(branchId, { search, pageSize: 10 });
  const found = matches.data?.items ?? [];
  const options: PickerOption[] = found.map((item) => ({
    id: String(item.id),
    primary: item.name,
    secondary: `${item.code} · ${item.categoryName}`,
  }));
  const remove = removable ? (
    <button
      type="button"
      disabled={busy}
      onClick={() => onChange((draft) => removeReceiveLine(draft, line.key))}
      className="text-default hover:text-danger cursor-pointer text-[length:var(--scale-list-meta)] font-semibold disabled:cursor-not-allowed"
    >
      Remove this design
    </button>
  ) : null;

  if (line.itemId === null || changing) {
    return (
      <Card>
        <Field label={changing ? 'Another design' : 'The design that came in'}>
          <SearchPicker
            value={null}
            options={options}
            total={matches.data?.total ?? 0}
            loading={matches.isLoading}
            disabled={busy}
            placeholder="Search designs by name or code"
            onSearch={setSearch}
            onSelect={(option) => {
              const picked = found.find((item) => String(item.id) === option?.id);

              if (picked) {
                onPick(picked);
                setChanging(false);
              }
            }}
          />
        </Field>
        <div className="mt-2 flex justify-end gap-3">
          {changing && (
            <button
              type="button"
              onClick={() => setChanging(false)}
              className="text-default cursor-pointer text-[length:var(--scale-list-meta)] font-semibold"
            >
              Keep {design?.name ?? 'the design'}
            </button>
          )}
          {!changing && remove}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-3">
        {/* DesignPhoto fills its parent's width, so the box sets the size. */}
        <div className="w-11 shrink-0 overflow-hidden rounded">
          <DesignPhoto
            url={design?.primaryImageUrl ?? null}
            alt={design?.name ?? 'Design'}
            categoryPath={design?.categoryPath ?? ''}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-0 truncate text-[15px] font-semibold text-gray-900">
            {design?.name ?? 'Reading the design…'}
          </p>
          {design && (
            <p className="text-default mb-0 truncate text-[length:var(--scale-list-meta)]">
              {design.code} · {design.categoryName}
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => setChanging(true)}
          className="text-primary cursor-pointer text-[13px] font-semibold hover:underline"
        >
          Change
        </button>
      </div>

      {axes === undefined && (
        <p className="text-default mt-3 text-[length:var(--scale-list-meta)]">
          Reading its sizes and colours…
        </p>
      )}
      {axes && axes.length === 0 && (
        <div className="mt-3">
          <Notice tone="warning" title="This design cannot take stock yet">
            Its category has nothing that tells its garments apart. Add a size to the category
            first.
          </Notice>
        </div>
      )}

      {axes && axes.length > 0 && (
        <CountColours line={line} axes={axes} busy={busy} onChange={onChange} />
      )}

      {/* Cost and rent on the card itself, as on the phone (Option A, 2026-10-17). */}
      <div className="border-border-color mt-4 grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-3">
        <Field label={`Cost each (${currency})`}>
          <MoneyInput
            value={line.purchaseCost}
            disabled={busy}
            onChange={(purchaseCost) =>
              onChange((draft) => updateReceiveLine(draft, line.key, { purchaseCost }))
            }
          />
        </Field>
        {mayRate && (
          <>
            <Field
              label={`Rent for the event (${currency})`}
              hint="Empty leaves its rate card to price it."
            >
              <MoneyInput
                value={line.rentForEvent}
                disabled={busy}
                onChange={(rentForEvent) =>
                  onChange((draft) => updateReceiveLine(draft, line.key, { rentForEvent }))
                }
              />
            </Field>
            <Field
              label={`Each extra day (${currency})`}
              hint="Optional. Empty: one rent however long."
            >
              <MoneyInput
                value={line.extraDayRent}
                disabled={busy}
                onChange={(extraDayRent) =>
                  onChange((draft) => updateReceiveLine(draft, line.key, { extraDayRent }))
                }
              />
            </Field>
          </>
        )}
      </div>

      <div className="mt-4">
        <Heading>Condition</Heading>
        <Tiles
          options={conditions.map((condition) => ({ id: condition, label: condition }))}
          value={line.condition}
          disabled={busy}
          onChange={(condition) =>
            onChange((draft) => updateReceiveLine(draft, line.key, { condition }))
          }
        />
      </div>

      {photos && (
        <div className="border-border-color mt-4 border-t pt-4">
          <Heading>Photos of this design</Heading>
          <div className="flex flex-wrap gap-2">
            {photos.map((file, index) => (
              <button
                key={`${file.name}-${index}`}
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => onPhotos(photos.filter((other) => other !== file))}
                className="bg-light relative h-19 w-15 cursor-pointer overflow-hidden rounded-md"
              >
                <img src={URL.createObjectURL(file)} alt="" className="size-full object-cover" />
                <span className="bg-dark absolute top-0.5 right-0.5 flex size-5 items-center justify-center rounded-full text-white">
                  <i className="icon icon-x text-[11px]" />
                </span>
              </button>
            ))}
            <label className="border-border-color hover:bg-light flex h-19 cursor-pointer items-center gap-2 rounded-md border bg-white px-4 text-[13px] font-semibold text-gray-900">
              <i className="icon icon-camera text-primary" />
              {photos.length > 0 ? 'Add more' : 'Add photos'}
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={busy}
                className="hidden"
                onChange={(event) => {
                  const chosen = [...(event.target.files ?? [])];
                  event.target.value = '';
                  onPhotos([...photos, ...chosen]);
                }}
              />
            </label>
          </div>
          <p className="text-default mt-1.5 mb-0 text-[length:var(--scale-list-meta)]">
            The whole garment against a plain wall, in daylight. They are added once it is booked
            in.
          </p>
        </div>
      )}

      {remove && <div className="mt-3 flex justify-end">{remove}</div>}
    </Card>
  );
}

/**
 * Option C of canvas "Stock in by colour and size" (owner, 2026-10-31): pick a colour, tick the
 * sizes that came, say how many of each and Add — then the next colour. What was added is a list of
 * colour-and-size rows, each with its own count.
 */
function CountColours({
  line,
  axes,
  busy,
  onChange,
}: {
  line: ReceiveLine;
  axes: ItemVariantAxisOptions[];
  busy: boolean;
  onChange: (change: (draft: ReceiveDraft) => ReceiveDraft) => void;
}) {
  const { looks } = splitAxes(axes);
  const sizes = sizeChoices(axes);
  const [look, setLook] = useState<Record<number, number>>({});
  const [ticked, setTicked] = useState<readonly string[]>([]);
  const [each, setEach] = useState(1);
  // A design of one size has nothing to tick: that size is always the one being added.
  const chosenSizes = sizes.length === 1 ? [sizes[0]!.key] : ticked;
  const lookChosen = looks.every((axis) => look[axis.attributeId] !== undefined);
  const canAdd = lookChosen && chosenSizes.length > 0 && each > 0;
  const name = lookName(look, axes);
  const sizeLabels = sizes.filter((size) => chosenSizes.includes(size.key)).map((s) => s.label);
  const rows = line.added.flatMap((entry) =>
    sizes
      .filter((size) => (entry.counts[size.key] ?? 0) > 0)
      .map((size) => ({ entry, size, count: entry.counts[size.key]! })),
  );
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  const add = () => {
    if (!canAdd) {
      return;
    }

    onChange((draft) => addSizes(draft, line.key, look, chosenSizes, each));
    setTicked([]);
    setEach(1);
  };

  return (
    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-4">
        {looks.map((axis) => (
          <div key={axis.attributeId}>
            <Heading>{axis.attributeName} · one</Heading>
            <Tiles
              options={axis.values.map((value) => ({
                id: value.valueId,
                label: value.name,
                swatch: value.colourHex,
              }))}
              value={look[axis.attributeId] ?? null}
              disabled={busy}
              onChange={(valueId) => setLook((was) => ({ ...was, [axis.attributeId]: valueId }))}
            />
          </div>
        ))}

        {sizes.length > 1 && (
          <div>
            <Heading>Sizes · tick one or more</Heading>
            <div className="flex flex-wrap gap-2">
              {sizes.map((size) => {
                const on = ticked.includes(size.key);

                return (
                  <button
                    key={size.key}
                    type="button"
                    aria-pressed={on}
                    disabled={busy}
                    onClick={() =>
                      setTicked((was) =>
                        on ? was.filter((key) => key !== size.key) : [...was, size.key],
                      )
                    }
                    className={`min-w-14 cursor-pointer rounded-md px-3 py-2 text-[15px] font-bold disabled:cursor-not-allowed ${
                      on
                        ? 'border-primary bg-primary-transparent border-2 text-gray-900'
                        : 'border-border-color hover:bg-light border bg-white text-gray-900'
                    }`}
                  >
                    {size.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <span className="text-[15px] font-semibold text-gray-900">
            {chosenSizes.length > 1 ? 'How many of each' : 'How many'}
          </span>
          <Counter
            value={each}
            name="each size"
            disabled={busy}
            onChange={(next) => setEach(Math.max(1, next))}
          />
        </div>

        <Button
          variant="primary"
          icon="icon icon-plus"
          className="w-full justify-center"
          loading={busy}
          disabled={!canAdd || busy}
          onClick={add}
        >
          {!lookChosen
            ? `Choose ${looks.map((axis) => axis.attributeName.toLowerCase()).join(' and ')}`
            : chosenSizes.length === 0
              ? 'Tick a size to add'
              : `Add ${[name, sizes.length > 1 ? sizeLabels.join(', ') : null]
                  .filter(Boolean)
                  .join(' ')} — ${each}${chosenSizes.length > 1 ? ' each' : ''}`}
        </Button>
      </div>

      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <Heading>Counted</Heading>
          {total > 0 && (
            <span className="text-primary text-[13px] font-bold">
              {total} garment{total === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="border-border-color text-default rounded-md border border-dashed p-4 text-center text-[length:var(--scale-list-meta)]">
            Nothing counted yet. Pick {looks.length > 0 ? 'a colour and ' : ''}sizes, then Add.
          </p>
        ) : (
          <ul className="border-border-color overflow-hidden rounded-md border ps-0">
            {rows.map(({ entry, size, count }, index) => {
              const key = lookKey(entry.look);
              const label = [lookName(entry.look, axes), sizes.length > 1 ? size.label : null]
                .filter(Boolean)
                .join(' · ');

              return (
                <li
                  key={`${key}|${size.key}`}
                  className={`flex list-none items-center gap-3 py-1.5 ps-3 pe-1.5 ${
                    index > 0 ? 'border-border-color border-t' : ''
                  }`}
                >
                  <Swatch hex={lookColour(entry.look, axes)} />
                  <span className="flex-1 truncate text-[15px] font-semibold text-gray-900">
                    {label || 'One size'}
                  </span>
                  <Counter
                    value={count}
                    name={label}
                    disabled={busy}
                    onChange={(next) =>
                      onChange((draft) =>
                        setAddedCount(draft, line.key, key, size.key, Math.max(1, next)),
                      )
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${label}`}
                    disabled={busy}
                    onClick={() =>
                      onChange((draft) => setAddedCount(draft, line.key, key, size.key, 0))
                    }
                    className="text-default hover:text-danger flex size-9 cursor-pointer items-center justify-center disabled:cursor-not-allowed"
                  >
                    <i className="icon icon-x" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** A colour's dot beside its name; nothing for a look with no colour. */
function Swatch({ hex }: { hex: string | null | undefined }) {
  return hex ? (
    <span
      aria-hidden="true"
      className="inline-block size-3.5 shrink-0 rounded-full ring-1 ring-black/15"
      style={{ backgroundColor: hex }}
    />
  ) : null;
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-default mb-2 text-[11px] font-bold tracking-[0.12em] uppercase">
      {children}
    </p>
  );
}

/** Choice tiles — the chosen one a `<div>`, never a disabled button. */
function Tiles<T extends string | number>({
  options,
  value,
  disabled,
  onChange,
}: {
  options: { id: T; label: string; swatch?: string | null }[];
  value: T | null;
  disabled: boolean;
  onChange: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) =>
        option.id === value ? (
          <div
            key={option.id}
            aria-current="true"
            className="border-primary bg-primary-transparent flex items-center gap-2 rounded-md border-2 px-3.5 py-2 text-[13px] font-semibold text-gray-900"
          >
            <Swatch hex={option.swatch} />
            {option.label}
          </div>
        ) : (
          <button
            key={option.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.id)}
            className="border-border-color hover:bg-light flex cursor-pointer items-center gap-2 rounded-md border bg-white px-3.5 py-2 text-[13px] text-gray-900 disabled:cursor-not-allowed"
          >
            <Swatch hex={option.swatch} />
            {option.label}
          </button>
        ),
      )}
    </div>
  );
}

/** − count + — tappable like the phone's stepper, and typeable for a box of forty. */
function Counter({
  value,
  name,
  disabled,
  onChange,
}: {
  value: number;
  name: string;
  disabled: boolean;
  onChange: (next: number) => void;
}) {
  const set = (next: number) => onChange(Math.min(maxPerSize, Math.max(0, next)));
  const step =
    'border-border-color hover:bg-light flex size-9 cursor-pointer items-center justify-center rounded-md border bg-white text-gray-900 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <span className="flex items-center gap-1.5">
      <button
        type="button"
        aria-label={`One fewer of ${name}`}
        disabled={disabled || value === 0}
        onClick={() => set(value - 1)}
        className={step}
      >
        <i className="icon icon-minus" />
      </button>
      <input
        type="number"
        min={0}
        max={maxPerSize}
        aria-label={`How many of ${name}`}
        value={value === 0 ? '' : value}
        placeholder="0"
        disabled={disabled}
        onChange={(event) => set(Number(event.target.value) || 0)}
        onWheel={(event) => event.currentTarget.blur()}
        className="border-border-color w-14 rounded-md border bg-white px-2 py-1.5 text-center text-[15px] font-semibold text-gray-900"
      />
      <button
        type="button"
        aria-label={`One more of ${name}`}
        disabled={disabled || value >= maxPerSize}
        onClick={() => set(value + 1)}
        className={step}
      >
        <i className="icon icon-plus" />
      </button>
    </span>
  );
}

function MoneyInput({
  value,
  disabled,
  onChange,
}: {
  value: number | null;
  disabled: boolean;
  onChange: (value: number | null) => void;
}) {
  return (
    <input
      type="number"
      min={0}
      step="0.01"
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => onChange(typedNumber(event.target.value))}
      onWheel={(event) => event.currentTarget.blur()}
      className={controlClass}
    />
  );
}
